// Admin System Info Section - Environment and device detection information
// Sub-admin page for viewing system information and capabilities

import React from 'react';
import { MainLayout } from '../../../../components/Layout';
import { AdminPageHeader } from '../../components';
import { envUtils } from '../../../../core/utils';
import './AdminSystemInfoSection.css';

/**
 * AdminSystemInfoSection - Shows environment detection information
 */
export function AdminSystemInfoSection() {
  // Get environment info using our new utils
  const deviceInfo = envUtils.device.getDeviceInfo();
  const environmentType = envUtils.device.getEnvironmentType();

  const getEnvironmentType = () => {
    switch (environmentType) {
      case 'desktop':
        return {
          title: "Desktop Application (Electron)",
          icon: "fa-desktop",
          color: "#007acc",
          description: "Running as native desktop application with enhanced capabilities"
        };
      case 'mobile':
        return {
          title: "Mobile Device",
          icon: "fa-mobile-alt",
          color: "#e74c3c",
          description: "Mobile device with touch-optimized interface"
        };
      case 'website':
      default:
        return {
          title: "Web Browser",
          icon: "fa-globe",
          color: "#27ae60",
          description: "Standard web browser environment"
        };
    }
  };

  const getCapabilities = () => {
    const caps = [];
    if (deviceInfo.capabilities.hasLocalAI) {
      caps.push({
        name: "Local AI Models",
        description: "Can run AI models locally without cloud dependency",
        icon: "fa-brain",
        available: true
      });
    }
    if (deviceInfo.capabilities.hasFileSystem) {
      caps.push({
        name: "Direct File Access",
        description: "Full file system access for reading/writing files",
        icon: "fa-folder-open",
        available: true
      });
    }
    if (deviceInfo.capabilities.hasNativeMenus) {
      caps.push({
        name: "Native Window Controls",
        description: "Native OS window management and menus",
        icon: "fa-window-maximize",
        available: true
      });
    }
    if (deviceInfo.capabilities.isEmbedded) {
      caps.push({
        name: "Embedded Web App",
        description: "Web application embedded in native container",
        icon: "fa-box",
        available: true
      });
    }

    // Add standard web capabilities if no special capabilities
    if (caps.length === 0) {
      caps.push({
        name: "Web Browser Capabilities",
        description: "Standard web browser features and limitations",
        icon: "fa-chrome",
        available: false
      });
    }

    return caps;
  };

  const environment = getEnvironmentType();
  const capabilities = getCapabilities();

  return (
    <MainLayout contentClassName="main-content--admin">
      <AdminPageHeader
        title="System Information"
        subtitle="Environment detection and device capabilities"
        icon="fa-info-circle"
      />

      <div className="admin-system-info-container">
        <div className="admin-section-content">
          <div className="admin-system-info-content">
          {/* Environment Overview */}
          <div className="system-info-card environment-card">
            <div className="card-header">
              <div className="card-icon" style={{ color: environment.color }}>
                <i className={`fa-solid ${environment.icon}`}></i>
              </div>
              <div className="card-title-section">
                <h3>Environment Type</h3>
                <h2 style={{ color: environment.color }}>{environment.title}</h2>
                <div className="environment-badge" style={{ background: environment.color }}>
                  {environmentType.toUpperCase()}
                </div>
              </div>
            </div>
            <p className="environment-description">{environment.description}</p>
          </div>

          {/* Device Information */}
          <div className="system-info-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fa-solid fa-mobile-screen-button"></i>
              </div>
              <h3>Device Information</h3>
            </div>
            <div className="device-info-grid">
              <div className="info-item">
                <span className="info-label">Device Type:</span>
                <span className="info-value">{deviceInfo.device.isMobile ? 'Mobile' : 'Desktop'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Touch Support:</span>
                <span className="info-value">{deviceInfo.device.isTouch ? 'Yes' : 'No'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Screen Size:</span>
                <span className="info-value">{deviceInfo.screen.width} × {deviceInfo.screen.height}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Mobile Screen:</span>
                <span className="info-value">{deviceInfo.screen.isMobileSize ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {/* Special Capabilities */}
          <div className="system-info-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fa-solid fa-star"></i>
              </div>
              <h3>Special Capabilities</h3>
            </div>
            <div className="capabilities-list">
              {capabilities.map((capability, index) => (
                <div key={index} className={`capability-item ${capability.available ? 'available' : 'unavailable'}`}>
                  <div className="capability-icon">
                    <i className={`fa-solid ${capability.icon}`}></i>
                  </div>
                  <div className="capability-content">
                    <h4>{capability.name}</h4>
                    <p>{capability.description}</p>
                  </div>
                  <div className="capability-status">
                    {capability.available ? (
                      <span className="status-badge available">✅ Available</span>
                    ) : (
                      <span className="status-badge unavailable">❌ Standard Web</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Environment Type Logic */}
          <div className="system-info-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fa-solid fa-cogs"></i>
              </div>
              <h3>Environment Detection Logic</h3>
            </div>
            <div className="detection-logic">
              <div className="logic-step">
                <span className="step-number">1</span>
                <span className="step-description">
                  Check for Desktop API (<code>window.navigationAPI</code>)
                </span>
                <span className={`step-result ${environmentType === 'desktop' ? 'active' : ''}`}>
                  {deviceInfo.capabilities.hasElectronAPI ? '✅ Found → DESKTOP' : '❌ Not Found → Continue'}
                </span>
              </div>

              <div className="logic-step">
                <span className="step-number">2</span>
                <span className="step-description">
                  User Agent Mobile Pattern Match
                </span>
                <span className={`step-result ${environmentType === 'mobile' ? 'active' : ''}`}>
                  {deviceInfo.device.isMobile ? '✅ Mobile Device → MOBILE' : '❌ Desktop Device → Continue'}
                </span>
              </div>

              <div className="logic-step">
                <span className="step-number">3</span>
                <span className="step-description">
                  Default to Web Browser
                </span>
                <span className={`step-result ${environmentType === 'website' ? 'active' : ''}`}>
                  ✅ WEBSITE
                </span>
              </div>

              <div className="logic-summary">
                <h4>Result: <span className="environment-result">{environmentType.toUpperCase()}</span></h4>
              </div>
            </div>
          </div>

          {/* UI Mode Status */}
          <div className="system-info-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fa-solid fa-paint-brush"></i>
              </div>
              <h3>UI Mode Status</h3>
            </div>
            <div className="ui-mode-status">
              <div className="current-mode">
                <h4>Currently Active:</h4>
                <span className={`mode-badge ${deviceInfo.ui.shouldUseMobileUI ? 'mobile' : 'desktop'}`}>
                  {deviceInfo.ui.shouldUseMobileUI ? '📱 Mobile UI' : '🖥️ Desktop UI'}
                </span>
              </div>
              <div className="mode-explanation">
                <p>
                  {deviceInfo.ui.shouldUseMobileUI
                    ? "Mobile UI is active due to device type and/or screen size, providing touch-friendly controls and compact layouts."
                    : "Desktop UI is active, providing full feature set with expanded layouts and additional controls."
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
}

export default AdminSystemInfoSection;